import React, { useState, useRef, useEffect } from "react";
import {
  Form,
  Button,
  Alert,
  Spinner,
  Card,
  Table,
  ProgressBar,
} from "react-bootstrap";
// import "./styles.module.css";
import { Box } from "./brokenLinkStyle";
import { ToolsHeader } from "../components/ToolsHeader";
import { isInIframe } from "../../utils/is-in-iframe";

interface CheckResult {
  status: string;
  url: string;
  anchorText: string;
  linkedFrom: string;
  isBroken?: boolean;
  type?: string;
}

const getProblematicLinks = (results: CheckResult[]) =>
  results.filter(
    (link) =>
      link.isBroken ||
      link.status.startsWith("4") ||
      link.status.startsWith("5") ||
      link.status.includes("DNS") ||
      link.status.includes("Timeout") ||
      link.status.includes("Network") ||
      link.status.includes("SSL")
  );


const BrokenLinkChecker: React.FC = () => {
  const [url, setUrl] = useState<string>("");
  const [checkMode, setCheckMode] = useState<"single" | "whole">("single");
  const [allResults, setAllResults] = useState<CheckResult[]>([]);
  const [displayedResults, setDisplayedResults] = useState<CheckResult[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>("");
  const [progress, setProgress] = useState<number>(0);
  const [totalUrls, setTotalUrls] = useState<number>(0);
  const [checkedUrls, setCheckedUrls] = useState<number>(0);
  const [okCount, setOkCount] = useState<number>(0);
  const [failedCount, setFailedCount] = useState<number>(0);
  const [botBlockedCount, setBotBlockedCount] = useState<number>(0);
  const [sslErrorCount, setSslErrorCount] = useState<number>(0);
  const [showFullReport, setShowFullReport] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const tableRef = useRef<HTMLTableElement>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isChecking || !url) return;

    setIsChecking(true);
    setIsLoading(true);
    setError("");
    setAllResults([]);
    setDisplayedResults([]);
    setCheckedUrls(0);
    setTotalUrls(0);
    setOkCount(0);
    setFailedCount(0);
    setBotBlockedCount(0);
    setSslErrorCount(0);
    setProgress(0);
    setShowFullReport(false);

    try {
      const baseUrl = process.env.REACT_APP_BASE_API!;

      if (checkMode === "single") {
        const apiUrl = `${baseUrl}/api/site-map?url=${encodeURIComponent(url)}`;
        const response = await fetch(apiUrl);
        const data = await response.json();

        if (!response.ok || !data.success || !Array.isArray(data.data)) {
          throw new Error("Failed to fetch or parse links.");
        }

        const linkResults: CheckResult[] = data.data.map((item: any) => {
          const isActuallyBroken =
            item.isBroken ||
            item.status.startsWith("4") ||
            item.status.startsWith("5") ||
            item.status.includes("DNS") ||
            item.status.includes("SSL") ||
            item.status.includes("Connection") ||
            item.status.includes("Timeout") ||
            item.status.includes("Network");

          return {
            url: item.link,
            status: item.status,
            isBroken: isActuallyBroken, // ✅ THIS is the key
            anchorText: item.anchorText,
            linkedFrom: url,
            type:
              item.status.startsWith("403")
                ? "Bot Blocked"
                : item.status.startsWith("4") || item.status.startsWith("5")
                  ? "HTTP Error"
                  : item.status.includes("SSL")
                    ? "SSL Error"
                    : item.status.includes("DNS")
                      ? "DNS Error"
                      : item.status.includes("Timeout") || item.status.includes("Network")
                        ? "Network Error"
                        : "Other",
          };
        });


        setAllResults(linkResults);
        setDisplayedResults(getProblematicLinks(linkResults)); // ✅ broken first



        setTotalUrls(linkResults.length);
        setCheckedUrls(linkResults.length);
        setOkCount(linkResults.filter((l) => !l.isBroken && !l.status.includes('Bot Blocked') && !l.status.includes('SSL')).length);
        setFailedCount(linkResults.filter((l) => l.isBroken).length);

        setBotBlockedCount(linkResults.filter((l) => l.status.includes('Bot Blocked')).length);
        setSslErrorCount(linkResults.filter((l) => l.status.startsWith('-1') || l.status.includes('SSL')).length);
        setProgress(100);
      } else {
        // Whole site mode (apply same logic)
        const apiUrl = `${baseUrl}/api/site-map/allLink?url=${encodeURIComponent(url)}`;
        const response = await fetch(apiUrl);
        const data2 = await response.json();

        if (data2.data && Array.isArray(data2.data)) {
          const totalPages = data2.data.length;
          let runningResults: CheckResult[] = [];
          const concurrency = 5;

          for (let i = 0; i < totalPages; i += concurrency) {
            const batch = data2.data.slice(i, i + concurrency);
            const batchPromises = batch.map(async (pageUrl: string) => {
              try {
                const eUrl = `${baseUrl}/api/site-map?url=${encodeURIComponent(pageUrl)}`;
                const res = await fetch(eUrl);
                const resData = await res.json();

                if (!res.ok || !resData.success || !Array.isArray(resData.data)) {
                  return [];
                }

                return resData.data.map((item: any) => {
                  const isActuallyBroken =
                    item.isBroken ||
                    item.status.startsWith("4") ||
                    item.status.startsWith("5") ||
                    item.status.includes("DNS") ||
                    item.status.includes("SSL") ||
                    item.status.includes("Connection") ||
                    item.status.includes("Timeout") ||
                    item.status.includes("Network");

                  return {
                    url: item.link,
                    status: item.status,
                    isBroken: isActuallyBroken,
                    anchorText: item.anchorText || "(no anchor text)",
                    linkedFrom: pageUrl,
                    type:
                      item.status.startsWith("403")
                        ? "Bot Blocked"
                        : item.status.startsWith("4") || item.status.startsWith("5")
                          ? "HTTP Error"
                          : item.status.includes("SSL")
                            ? "SSL Error"
                            : item.status.includes("DNS")
                              ? "DNS Error"
                              : item.status.includes("Timeout") || item.status.includes("Network")
                                ? "Network Error"
                                : "Other",
                  };
                });
              } catch (err) {
                return [];
              }
            });



            const batchResults = await Promise.all(batchPromises);
            const flatBatch = batchResults.flat();
            runningResults = [...runningResults, ...flatBatch];

            const uniqueResults = Array.from(
              new Map(runningResults.map((item) => [item.url, item])).values()
            );

            setAllResults(uniqueResults);
            setDisplayedResults(getProblematicLinks(uniqueResults)); // ✅ broken first


            setCheckedUrls(uniqueResults.length);
            setOkCount(uniqueResults.filter((l) => !l.isBroken && !l.status.includes('Bot Blocked') && !l.status.includes('SSL')).length);
            setFailedCount(uniqueResults.filter((l) => l.isBroken).length);

            setBotBlockedCount(uniqueResults.filter((l) => l.status.includes('Bot Blocked')).length);
            setSslErrorCount(uniqueResults.filter((l) => l.status.startsWith('-1') || l.status.includes('SSL')).length);

            const checkedPages = Math.min(i + concurrency, totalPages);
            const progressPercent = Math.round((checkedPages / totalPages) * 100);
            setProgress(progressPercent);
          }

          const uniqueFinal = Array.from(
            new Map(runningResults.map((item) => [item.url, item])).values()
          );
          setAllResults(uniqueFinal);
          setDisplayedResults(getProblematicLinks(uniqueFinal));

          setTotalUrls(uniqueFinal.length);
          setCheckedUrls(uniqueFinal.length);
          setOkCount(uniqueFinal.filter((l) => !l.isBroken && !l.status.includes('Bot Blocked') && !l.status.includes('SSL')).length);
          setFailedCount(uniqueFinal.filter((l) => l.isBroken).length);

          setBotBlockedCount(uniqueFinal.filter((l) => l.status.includes('Bot Blocked')).length);
          setSslErrorCount(uniqueFinal.filter((l) => l.status.startsWith('-1') || l.status.includes('SSL')).length);
          setProgress(100);
        } else {
          throw new Error("No pages found for analysis.");
        }
      }

      const height = document.body.scrollHeight;
      window.parent.postMessage({ type: "setHeight", height }, "*");
    } catch (err: any) {
      const errorMessage = err instanceof Error ? err.message : "An error occurred";
      setError(errorMessage);
    } finally {
      setIsLoading(false);
      setIsChecking(false);
    }
  };

  const handleFullReport = () => {
    setShowFullReport(true);
    setDisplayedResults(allResults); // ✅ show EVERYTHING
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  };


  useEffect(() => {
    const height = document.body.scrollHeight;
    window.parent.postMessage({ type: "setHeight", height }, "*");
  }, [displayedResults]);

  const renderStatus = () => {
    if (!isLoading && allResults.length > 0) {
      return (
        <div className="status">
          <p>
            {progress}% scanned - {checkedUrls}/{totalUrls} URLs checked,{" "}
            {okCount} OK, {failedCount} true broken, {botBlockedCount} bot blocked, {sslErrorCount} SSL errors
          </p>
          <p>Generated: {new Date().toLocaleString()}</p>
          {!showFullReport && (
            <Button
              variant="outline-primary"
              onClick={handleFullReport}
              className="fullReportButton"
            >
              Full report
            </Button>
          )}
        </div>
      );
    }
    return null;
  };

  return (
    <>
      <Box className={`${!isInIframe ? 'py-5 defaultText' : 'py-1 interDisplay'}`}>
        {!isInIframe && (
          <ToolsHeader
            title="Site Checker: Free Broken Link Tool"
            description="Quickly identify and fix broken links on your website to boost
                SEO and user experience."
          />
        )}
        <Card className="container">
          <Card.Body>
            <Form onSubmit={handleSubmit}>
              <Form.Group controlId="url" className="mb-3">
                <Form.Control
                  type="url"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  required
                  disabled={isLoading}
                  className="input"
                  placeholder="Enter website URL (e.g., https://example.com)"
                />
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Check
                  inline
                  label="Check single webpage"
                  name="checkMode"
                  type="radio"
                  id="check-single"
                  checked={checkMode === "single"}
                  onChange={() => setCheckMode("single")}
                />
                <Form.Check
                  inline
                  label="Check whole website"
                  name="checkMode"
                  type="radio"
                  id="check-whole"
                  checked={checkMode === "whole"}
                  onChange={() => setCheckMode("whole")}
                />
              </Form.Group>

              <Button
                variant="warning"
                type="submit"
                disabled={isLoading || !url}
                className="submitButton"
                style={{ backgroundColor: "#8cc540", color: "#fff" }}
              >
                {isLoading ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    Checking...
                  </>
                ) : (
                  "Check"
                )}
              </Button>
            </Form>

            {isLoading && (
              <ProgressBar
                now={progress}
                label={`${progress}%`}
                className="mt-3"
              />
            )}

            {error && (
              <Alert variant="danger" className="mt-3">
                {error}
              </Alert>
            )}

            {renderStatus()}

            {displayedResults.length > 0 && (
              <div style={{ width: "100%", overflow: "auto" }}>
                <Table striped bordered hover className="table" ref={tableRef}>
                  <thead>
                    <tr>
                      <th>Status</th>
                      <th>Type</th>
                      <th>URL</th>
                      <th>Anchor Text</th>
                      <th>Linked From</th>
                    </tr>
                  </thead>
                  <tbody>
                    {displayedResults.map((result, index) => (
                      <tr
                        key={index}
                        className={
                          result.isBroken
                            ? "broken"
                            : result.status === "Checking"
                              ? "checking"
                              : ""
                        }
                      >
                        <td>{result.status}</td>
                        <td>{result.type}</td>
                        <td>
                          <a
                            href={result.url}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {result.url}
                          </a>
                        </td>
                        <td>{result.anchorText}</td>
                        <td>{result.linkedFrom}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              </div>
            )}
          </Card.Body>
        </Card>
      </Box>
    </>
  );
};

export default BrokenLinkChecker;




// import React, { useState, useRef, useEffect } from "react";
// import {
//   Form,
//   Button,
//   Alert,
//   Spinner,
//   Card,
//   Table,
//   ProgressBar,
//   Badge,
// } from "react-bootstrap";
// import { Box } from "./brokenLinkStyle";
// import { ToolsHeader } from "../components/ToolsHeader";
// import { isInIframe } from "../../utils/is-in-iframe";

// interface CheckResult {
//   url: string;
//   status: string;
//   anchorText: string;
//   linkedFrom: string;
//   isBroken: boolean;
//   type: string;
// }

// const CONCURRENCY = 5;
// const MAX_PAGES = 50;

// const BrokenLinkChecker: React.FC = () => {
//   const [url, setUrl] = useState("");
//   const [checkMode, setCheckMode] = useState<"single" | "whole">("single");
//   const [viewMode, setViewMode] = useState<"seo" | "technical">("seo");

//   const [allResults, setAllResults] = useState<CheckResult[]>([]);
//   const [displayedResults, setDisplayedResults] = useState<CheckResult[]>([]);

//   const [isLoading, setIsLoading] = useState(false);
//   const [error, setError] = useState("");

//   const [progress, setProgress] = useState(0);
//   const [totalUrls, setTotalUrls] = useState(0);
//   const [checkedUrls, setCheckedUrls] = useState(0);

//   const [okCount, setOkCount] = useState(0);
//   const [warningCount, setWarningCount] = useState(0);
//   const [failedCount, setFailedCount] = useState(0);

//   const tableRef = useRef<HTMLTableElement>(null);

//   /* ---------------------------------------------
//      SEO IMPACT
//   --------------------------------------------- */
//   const getSeoImpact = (status?: string, isBroken?: boolean) => {
//     const s = status || "";
//     if (isBroken) return { label: "Critical", variant: "danger" };
//     if (s.includes("SSL") || s.includes("Blocked") || s.includes("Timeout")) {
//       return { label: "Warning", variant: "warning" };
//     }
//     return { label: "Safe", variant: "success" };
//   };

//   /* ---------------------------------------------
//      NORMALIZE LINK RESULTS
//   --------------------------------------------- */
//   const normalizeLinks = (
//     links: any[],
//     pageUrl: string
//   ): CheckResult[] =>
//     links.map((l) => {
//       const status = l.status || "200 OK";
//       const isBroken = Boolean(l.isBroken);

//       let type = "OK";
//       if (status.includes("SSL")) type = "Security";
//       else if (status.includes("Blocked")) type = "Access";
//       else if (isBroken) type = "HTTP Error";

//       return {
//         url: l.link || "",
//         status,
//         isBroken,
//         anchorText: l.anchorText || "link:href",
//         linkedFrom: pageUrl,
//         type,
//       };
//     });

//   /* ---------------------------------------------
//      SUBMIT
//   --------------------------------------------- */
//   const handleSubmit = async (e: React.FormEvent) => {
//     e.preventDefault();
//     if (!url || isLoading) return;

//     setIsLoading(true);
//     setError("");
//     setAllResults([]);
//     setDisplayedResults([]);
//     setProgress(0);
//     setCheckedUrls(0);
//     setTotalUrls(0);
//     setOkCount(0);
//     setWarningCount(0);
//     setFailedCount(0);

//     try {
//       const baseUrl = process.env.REACT_APP_BASE_API!;

//       /* ---------- SINGLE PAGE ---------- */
//       if (checkMode === "single") {
//         const res = await fetch(
//           `${baseUrl}/api/site-map?url=${encodeURIComponent(url)}`
//         );
//         const json = await res.json();

//         if (!json.success || !Array.isArray(json.data)) {
//           throw new Error("Failed to fetch page links");
//         }

//         const results = normalizeLinks(json.data, url);
//         finalizeResults(results);
//         return;
//       }

//       /* ---------- WHOLE WEBSITE ---------- */
//       const pagesRes = await fetch(
//         `${baseUrl}/api/site-map/allLink?url=${encodeURIComponent(url)}`
//       );
//       const pagesJson = await pagesRes.json();

//       if (!pagesJson.success || !Array.isArray(pagesJson.data)) {
//         throw new Error("Failed to discover pages");
//       }

//       const pages = pagesJson.data.slice(0, MAX_PAGES);
//       setTotalUrls(pages.length);

//       let aggregated: CheckResult[] = [];
//       const seenLinks = new Map<string, CheckResult>();

//       for (let i = 0; i < pages.length; i += CONCURRENCY) {
//         const batch = pages.slice(i, i + CONCURRENCY);

//         const batchResults = await Promise.all(
//           batch.map(async (pageUrl: string) => {
//             try {
//               const res = await fetch(
//                 `${baseUrl}/api/site-map?url=${encodeURIComponent(pageUrl)}`
//               );
//               const json = await res.json();
//               if (!json.success || !Array.isArray(json.data)) return [];
//               return normalizeLinks(json.data, pageUrl);
//             } catch {
//               return [];
//             }
//           })
//         );

//         for (const pageLinks of batchResults) {
//           for (const link of pageLinks) {
//             const existing = seenLinks.get(link.url);

//             if (!existing || link.isBroken) {
//               seenLinks.set(link.url, link);
//             }

//           }
//         }

//         aggregated = Array.from(seenLinks.values());
//         setCheckedUrls(Math.min(i + CONCURRENCY, pages.length));
//         setProgress(
//           Math.round((Math.min(i + CONCURRENCY, pages.length) / pages.length) * 100)
//         );
//         setAllResults(aggregated);
//       }

//       finalizeResults(aggregated);
//     } catch (err: any) {
//       setError(err.message || "Something went wrong");
//     } finally {
//       setIsLoading(false);
//     }
//   };

//   /* ---------------------------------------------
//      FINAL COUNTS + DISPLAY
//   --------------------------------------------- */
//   const finalizeResults = (results: CheckResult[]) => {
//     const critical = results.filter((r) => r.isBroken);
//     const warnings = results.filter(
//       (r) =>
//         !r.isBroken &&
//         (r.status.includes("SSL") ||
//           r.status.includes("Blocked") ||
//           r.status.includes("Timeout"))
//     );

//     setFailedCount(critical.length);
//     setWarningCount(warnings.length);
//     setOkCount(results.length - critical.length - warnings.length);

//     setAllResults(results);
//     setDisplayedResults(viewMode === "seo" ? critical : results);
//     setTotalUrls(results.length);
//     setCheckedUrls(results.length);
//     setProgress(100);
//   };

//   /* ---------------------------------------------
//      VIEW MODE
//   --------------------------------------------- */
//   useEffect(() => {
//     setDisplayedResults(
//       viewMode === "seo"
//         ? allResults.filter((r) => r.isBroken)
//         : allResults
//     );
//   }, [viewMode, allResults]);

//   /* ---------------------------------------------
//      RENDER
//   --------------------------------------------- */
//   return (
//     <Box className={`${!isInIframe ? "py-5" : "py-2"}`}>
//       {!isInIframe && (
//         <ToolsHeader
//           title="Site Checker: Free Broken Link Tool"
//           description="Audit broken links across your website the same way Google does."
//         />
//       )}

//       <Card className="container">
//         <Card.Body>
//           <Form onSubmit={handleSubmit}>
//             <Form.Group className="mb-3">
//               <Form.Control
//                 type="url"
//                 placeholder="Enter website URL"
//                 value={url}
//                 onChange={(e) => setUrl(e.target.value)}
//                 required
//               />
//             </Form.Group>

//             <Form.Group className="mb-3">
//               <Form.Check
//                 inline
//                 label="Check single webpage"
//                 checked={checkMode === "single"}
//                 onChange={() => setCheckMode("single")}
//               />
//               <Form.Check
//                 inline
//                 label="Check whole website"
//                 checked={checkMode === "whole"}
//                 onChange={() => setCheckMode("whole")}
//               />
//             </Form.Group>

//             <Button
//               type="submit"
//               disabled={isLoading}
//               style={{ background: "#8cc540", border: "none" }}
//             >
//               {isLoading ? (
//                 <>
//                   <Spinner size="sm" className="me-2" />
//                   Checking…
//                 </>
//               ) : (
//                 "Check"
//               )}
//             </Button>
//           </Form>

//           {isLoading && (
//             <ProgressBar animated striped now={progress} className="mt-3" />
//           )}

//           {error && <Alert variant="danger" className="mt-3">{error}</Alert>}

//           {totalUrls > 0 && (
//             <>
//               <div className="mt-4">
//                 <p>
//                   ✅ <strong>{okCount}</strong> Safe ·{" "}
//                   ⚠️ <strong>{warningCount}</strong> Warnings ·{" "}
//                   ❌ <strong>{failedCount}</strong> Critical SEO issues
//                 </p>

//                 <Alert variant="info">
//                   <strong>ℹ️ About warnings</strong>
//                   <p className="mb-0">
//                     SSL issues or blocked access on external sites are{" "}
//                     <strong>not SEO problems</strong>. Google only considers links
//                     broken when they return <strong>404 or 410</strong>.
//                   </p>
//                 </Alert>

//                 <div className="mb-3">
//                   <Button
//                     size="sm"
//                     variant={viewMode === "seo" ? "primary" : "outline-primary"}
//                     className="me-2"
//                     onClick={() => setViewMode("seo")}
//                   >
//                     SEO View
//                   </Button>
//                   <Button
//                     size="sm"
//                     variant={viewMode === "technical" ? "primary" : "outline-primary"}
//                     onClick={() => setViewMode("technical")}
//                   >
//                     Technical View
//                   </Button>
//                 </div>
//               </div>

//               {displayedResults.length > 0 && (
//                 <div style={{ width: "100%", overflowX: "auto" }}>
//                   <Table striped bordered hover ref={tableRef} style={{ minWidth: "1100px" }}>

//                     <thead>
//                       <tr>
//                         <th>Status</th>
//                         <th>Type</th>
//                         <th>SEO Impact</th>
//                         <th>URL</th>
//                         <th>Anchor Text</th>
//                         <th>Linked From</th>
//                       </tr>
//                     </thead>
//                     <tbody>
//                       {displayedResults.map((r, i) => {
//                         const impact = getSeoImpact(r.status, r.isBroken);
//                         return (
//                           <tr
//                             key={i}
//                             className={
//                               impact.label === "Critical"
//                                 ? "table-danger"
//                                 : impact.label === "Warning"
//                                   ? "table-warning"
//                                   : ""
//                             }
//                           >
//                             <td>{r.status}</td>
//                             <td>{r.type}</td>
//                             <td>
//                               <Badge bg={impact.variant}>{impact.label}</Badge>
//                             </td>
//                             <td style={{ maxWidth: "420px", wordBreak: "break-all" }}>
//                               <a
//                                 href={r.url}
//                                 target="_blank"
//                                 rel="noreferrer"
//                                 style={{ display: "inline-block" }}
//                               >
//                                 {r.url}
//                               </a>
//                             </td>

//                             <td>{r.anchorText}</td>
//                             <td>{r.linkedFrom}</td>
//                           </tr>
//                         );
//                       })}
//                     </tbody>
//                   </Table>
//                 </div>

//               )}
//             </>
//           )}
//         </Card.Body>
//       </Card>
//     </Box>
//   );
// };

// export default BrokenLinkChecker;
